<template>
  <!-- Чат поддержки - custom element из fakt-support (Vue 3, Shadow DOM), спека 3.3 -->
  <fakt-support-widget
    v-if="branchId && enabled"
    ref="widget"
    :token="token"
    :branch-id="branchId"
    :api-url="apiUrl"
    :ws-url="wsUrl"
    :quiet="quiet ? 'true' : false"
  />
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import localStorageService from '@/services/refresh.service'
  import RouteName from '@/models/enums/RouteName'
  import { SUPPORT_URL, SUPPORT_WIDGET_URL } from './config'

  const SCRIPT_ID = 'fakt-support-widget-js'
  // Оформление заказа: кнопка чата скрыта, тосты копятся до выхода в листинг (11-acceptance).
  const QUIET_ROUTES = ['newOrder', 'fastOrderEdit', 'orderEdit']
  const QUIET_LAYOUTS = ['order-layout', 'edit-order-layout', 'kitchen-layout']
  // CRM меняет токен при refresh без события: сверяемся раз в 30 секунд.
  const TOKEN_CHECK_MS = 30000

  export default {
    name: 'SupportWidget',
    data () {
      return {
        token: localStorageService.getToken(),
        apiUrl: `${SUPPORT_URL}/api`,
        wsUrl: SUPPORT_URL,
        tokenTimer: null,
        listenedElement: null,
      }
    },
    computed: {
      // enabled - ВРЕМЕННО: поддержка только в филиалах из VUE_APP_SUPPORT_BRANCH_IDS. Компонент живёт и при
      // выключенной поддержке, чтобы сбросить сессию в Electron-оболочке и кнопку чата; сам виджет не рендерится.
      ...mapGetters({ branchId: 'branches/getGlobalFilialId', enabled: 'support/enabled' }),
      ...mapState({ newAppealTick: state => state.support.newAppealTick }),
      quiet () {
        return QUIET_ROUTES.includes(this.$route.name) || QUIET_LAYOUTS.includes(this.$route.meta?.layout)
      },
      // В quiet виджет прячет кнопку сам (спека 3.3).
      chatButtonVisible () {
        return Boolean(this.branchId) && this.enabled && !this.quiet
      },
      // Сессия для запасного входа в поддержку из Electron-оболочки (T22): CRM упала - чат открывается с ней.
      desktopSession () {
        if (!this.enabled) return { token: null, branchId: null }
        const branchId = Number(this.branchId)
        return { token: this.token || null, branchId: Number.isInteger(branchId) && branchId > 0 ? branchId : null }
      },
    },
    watch: {
      chatButtonVisible: {
        immediate: true,
        handler (value) {
          this.$store.commit('support/setChatButtonVisible', value)
        },
      },
      branchId: {
        immediate: true,
        handler (id) {
          if (id && this.enabled) this.$store.dispatch('support/loadMe')
        },
      },
      desktopSession: {
        immediate: true,
        handler (session) {
          this.sendDesktopSession(session)
        },
      },
      // Шторка просит «Новое обращение»: у чата (T08) есть openNew(), у заглушки - нет.
      newAppealTick () {
        const widget = this.$refs.widget
        if (widget && typeof widget.openNew === 'function') widget.openNew()
        else this.$store.dispatch('support/openForm')
      },
    },
    mounted () {
      // Смена филиала в CRM - это reload страницы, поэтому enabled на время жизни компонента постоянен.
      if (this.enabled) this.loadScript()
      this.tokenTimer = setInterval(this.syncToken, TOKEN_CHECK_MS)
      this.$nextTick(this.listen)
    },
    updated () {
      // Элемент появляется, когда стал известен филиал: вешаем слушатели на него.
      this.listen()
    },
    beforeDestroy () {
      // Виджет живёт, пока есть пользователь: уничтожение - это выход из CRM, оболочка забывает токен.
      this.sendDesktopSession({ token: null, branchId: null })
      this.$store.commit('support/setChatButtonVisible', false)
      clearInterval(this.tokenTimer)
      this.unlisten()
    },
    methods: {
      loadScript () {
        if (document.getElementById(SCRIPT_ID)) return
        const script = document.createElement('script')
        script.id = SCRIPT_ID
        script.src = SUPPORT_WIDGET_URL
        script.async = true
        document.head.appendChild(script)
      },
      // window.faktDesktop есть только в Electron-оболочке CRM (crm-desktop-app-electron 2.4.0+).
      sendDesktopSession (session) {
        const desktop = window.faktDesktop
        if (desktop && typeof desktop.setSupportSession === 'function') desktop.setSupportSession(session)
      },
      syncToken () {
        const token = localStorageService.getToken()
        if (token !== this.token) this.token = token
      },
      listen () {
        const element = this.$refs.widget
        if (!element || element === this.listenedElement) return
        this.unlisten()
        element.addEventListener('fakt-support:open-appeal', this.openAppeal)
        element.addEventListener('fakt-support:open-appeals', this.openAppeals)
        element.addEventListener('fakt-support:unread', this.setUnread)
        this.listenedElement = element
      },
      unlisten () {
        if (!this.listenedElement) return
        this.listenedElement.removeEventListener('fakt-support:open-appeal', this.openAppeal)
        this.listenedElement.removeEventListener('fakt-support:open-appeals', this.openAppeals)
        this.listenedElement.removeEventListener('fakt-support:unread', this.setUnread)
        this.listenedElement = null
      },
      openAppeal (event) {
        const id = event.detail?.id
        if (!id) return
        const path = `/support/${id}`
        if (this.$route.path !== path) this.$router.push(path)
      },
      // «Все обращения ›» в чате (c03): страница «Обращения филиала» (p6), окно виджет закрыл сам.
      openAppeals () {
        if (this.$route.name !== RouteName.SupportAppeals) this.$router.push({ name: RouteName.SupportAppeals })
      },
      setUnread (event) {
        const count = Number(event.detail?.count)
        if (Number.isFinite(count)) this.$store.commit('support/setUnread', count)
      },
    },
  }
</script>
